import React from 'react';

import styled from '@emotion/styled';
import {Color} from '@nfq/colors';
import {Spacer, spacing} from '@nfq/react-grid';
import {AnimatePresence} from 'motion/react';
import * as motion from 'motion/react-m';

import {EnterFromZeroHeightAnimation} from '../../../animations/enterFromZeroHeight';
import {P} from '../../../layout/Text/Text';

import {useHints} from './useHints';

/**
 * Defines the properties for the `ErrorMessages` component, which allows users to select from predefined options.
 * The component supports default values, disabling, and custom dropdown positioning. It also provides a callback to handle value
 * changes and can be targeted in tests using a `testId`.
 */
export interface ComponentProps {
    /**
     * Array of error messages to display.
     */
    baseTestId: string;
    /**
     * Array of hints to display.
     */
    hints: string[] | null;
    /**
     * Type of the hint.
     */
    hintType?: 'error' | 'normal';
    /**
     * Left padding as a multiple of spacing.
     */
    paddingLeft?: number;
}

/**
 * The `FilterSelectInput` component renders a customizable select input with options, which can be disabled and allows users to
 * choose an option from a list. The component also provides a popover list of options with animation and handles the user's selection
 * with the provided `onChange` callback.
 *
 * @param props             The component props.
 * @param props.baseTestId  The base test ID for testing purposes.
 * @param props.hints       An array of hints to display.
 * @param props.hintType    The type of the hint.
 * @param props.paddingLeft Left padding as a multiple of spacing.
 * @returns A JSX element representing the filter select input with animated popover options.
 *
 * @example
 * ```tsx
 * <Hints
 *   hints={["Error 1", "Error 2"]}
 * />
 * ```
 */
const Hints = ({baseTestId, hints, hintType = 'normal', paddingLeft = 2}: ComponentProps) => {
    const {colors} = useHints();

    return (
        <AnimatePresence mode="sync">
            {(hints !== null && hints.length > 0) && (
                <HintWrapper
                    animate="enter"
                    exit="exit"
                    initial="initial"
                    variants={EnterFromZeroHeightAnimation}
                >
                    {hints.map(hint => (
                        <div key={hint} data-cy={`${baseTestId}-${hintType === 'error' ? 'error-message' : 'hint'}`}>
                            <Spacer y={1} />
                            <Hint $hintType={hintType} $paddingLeft={paddingLeft}>
                                <Color $color={hintType === 'error' ? colors.error : colors.textSecondary}>
                                    {hint}
                                </Color>
                            </Hint>
                        </div>
                    ))}

                </HintWrapper>
            )}
        </AnimatePresence>
    );
};

Hints.displayName = 'Hints';

export {Hints};

const HintWrapper = styled(motion.div)`
    height: 0;
    overflow: hidden;
`;

interface HintProps {
    $hintType: 'error' | 'normal';
    $paddingLeft: number;
}

const Hint = styled(P)<HintProps>`
    margin-left: ${({$paddingLeft}) => `${spacing($paddingLeft)}`};
`;